import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Book as BookIcon,
  Person as PersonIcon,
  School as ClassIcon,
  Settings as PrepSettingsIcon,
  Home as PrepHomeIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import OwlMascot from '../../../assets/貓頭鷹博士_雙手.gif';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';

export default function PreparationHome({ currentPhase, onPhaseChange }) {
  const navigate = useNavigate();
  const [preparationStatus, setPreparationStatus] = useState({
    hasStudents: false,
    hasLessonPackage: false,
    hasCommunity: false,
    hasCourseDispatch: false
  });
  const [counts, setCounts] = useState({
    students: 0,
    lessonPackages: 0,
    communities: 0,
    courseDispatches: 0
  });
  const [loadingStatus, setLoadingStatus] = useState(false);
  const TOUR_KEY = 'prep_dialog_tour_v1';
  const tourChipSx = {
    fontWeight: 'bold',
    cursor: 'pointer',
    px: 1.25,
    '& .MuiChip-label': { px: 1.25 },
    '& .MuiChip-icon': { ml: 0.75 } 
  };
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  
  const tourSteps = [
    {
      title: '歡迎來到課前準備',
      content:
        '這頁是「課前準備」總覽，下面四張卡片就是你開課前要完成的流程。' +
        '你也可以用「上方導覽列」切換階段（課前/課中/診斷），或用「側邊導覽列」快速進到各個管理頁面。'
    },
    {
      title: 'Step 1：先把學生準備好（學生管理）',
      content:
        '先新增或匯入學生名單，後續的社群/分組、派發課程都會用到。' 
    },
    {
      title: 'Step 2：挑好今天要上的內容（備課包）',
      content:
        '接著建立或選擇「備課包」，把教材、任務流程先整理好。' +
        '備課包可以重複使用：同一份內容之後也能派給不同社群，不用每次重做。'
    },
    {
      title: 'Step 3：設定社群與預設分組（社群管理）',
      content:
        '建立社群/班級並設定預設分組，決定「這次要上課的對象」。' +
        '如果學生名單還沒準備好，請先回到 Step 1。'
    },
    {
      title: 'Step 4：派發課程開始吧！（課程派發）',
      content:
        '最後把「備課包」與「社群」連結後，系統會自動把備課包內的任務帶入並派給該社群的學生。' +
        '完成派發後，你就可以進入「課中實施」開始帶活動。'
    }
  ];
  const totalSteps = tourSteps.length;
  const currentStepNumber = tourStep + 1; // 1-based
  const progressValue = Math.round((currentStepNumber / totalSteps) * 100);
  useEffect(() => {
    const seen = localStorage.getItem(TOUR_KEY);
    if (!seen) {
      setTourOpen(true);
    }
  }, []);
  
  const handleTourNext = () => {
    if (tourStep === tourSteps.length - 1) {
      localStorage.setItem(TOUR_KEY, '1');
      setTourOpen(false);
    } else {
      setTourStep(prev => prev + 1);
    }
  };
  const handleTourSkip = () => {
    localStorage.setItem(TOUR_KEY, '1');
    setTourOpen(false);
    setTourStep(0);
  };
  useEffect(() => {
    checkPreparationStatus();
  }, []);

  const checkPreparationStatus = async () => {
    const extractCount = (response, fallbackKeys = []) => {
      const getCount = (value) => {
        if (typeof value === 'number') return value;
        if (Array.isArray(value)) return value.length;
        return null;
      };

      const candidates = [];
      const pushCandidates = (source) => {
        if (!source || typeof source !== 'object') return;
        const possibleKeys = [
          'data',
          'rows',
          'list',
          'items',
          'records',
          'results',
          'students',
          'classes',
          'lessonPackages',
          'courseDispatches',
          'communities',
          ...fallbackKeys
        ];

        possibleKeys.forEach((key) => {
          if (source[key] !== undefined) {
            candidates.push(source[key]);
          }
        });

        if (typeof source.count === 'number') {
          candidates.push(source.count);
        }
      };

      const payload = response?.data;
      const initialCandidates = [
        payload,
        payload?.data,
        payload?.results,
        payload?.list,
        payload?.items
      ];

      for (const candidate of initialCandidates) {
        const parsed = getCount(candidate);
        if (parsed !== null) return parsed;
        pushCandidates(candidate);
      }

      for (const candidate of candidates) {
        const parsed = getCount(candidate);
        if (parsed !== null) return parsed;
        if (candidate && typeof candidate === 'object') {
          pushCandidates(candidate);
        }
      }

      return 0;
    };

    try {
      setLoadingStatus(true);
      const [
        studentsResp,
        lessonPackagesResp,
        communitiesResp,
        courseDispatchesResp
      ] = await Promise.all([
        axiosInstance.get(config.getTeacherStudents || 'api/teacher-students'),
        axiosInstance.get(config.getLessonPackages || 'api/lesson-packages'),
        axiosInstance.get(config.getClasses || 'api/classes'),
        axiosInstance.get(config.getCourseDispatches || 'api/course-dispatches')
      ]);

      const students = extractCount(studentsResp, ['students']);
      const lessonPackages = extractCount(lessonPackagesResp, ['lessonPackages']);
      const communities = extractCount(communitiesResp, ['classes', 'communities']);
      const courseDispatches = extractCount(courseDispatchesResp, ['courseDispatches']);

      setCounts({
        students,
        lessonPackages,
        communities,
        courseDispatches
      });

      setPreparationStatus({
        hasStudents: students > 0,
        hasLessonPackage: lessonPackages > 0,
        hasCommunity: communities > 0,
        hasCourseDispatch: courseDispatches > 0
      });
    } catch (error) {
      console.error('檢查準備狀態失敗:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const stepCards = useMemo(() => ([
    {
      key: 'students',
      title: '先把學生準備好',
      subtitle: '新增或匯入學生，才能指派任務給他們唷！',
      description: '完成條件：系統內至少有 1 位學生。',
      icon: PersonIcon,
      buttonText: '前往學生管理',
      color: '#ffcc80',
      path: '/teacher/preparation/students',
      metric: `${counts.students} 位學生`,
      completed: preparationStatus.hasStudents
    },
    {
      key: 'lessonPackages',
      title: '挑好今天要上的內容',
      subtitle: '建立或選擇備課包，整理好教材與任務。',
      description: '完成條件：至少建立 1 個備課包。',
      icon: BookIcon,
      buttonText: '管理備課包',
      color: '#90caf9',
      path: '/teacher/preparation/lesson-packages',
      metric: `${counts.lessonPackages} 個備課包`,
      completed: preparationStatus.hasLessonPackage
    },
    {
      key: 'communities',
      title: '設定班級與分組',
      subtitle: '建立社群、匯入學生，安排好小組分工。',
      description: '完成條件：至少建立 1 個社群／班級。',
      icon: ClassIcon,
      buttonText: '前往社群管理',
      color: '#a5d6a7',
      path: '/teacher/preparation/communities',
      metric: `${counts.communities} 個社群`,
      completed: preparationStatus.hasCommunity,
      disabled: !preparationStatus.hasStudents
    },
    {
      key: 'courseDispatches',
      title: '派發課程開始吧！',
      subtitle: '選定要開課的班級，派送任務與時間表。',
      description: '完成條件：至少派發 1 堂課程。',
      icon: SendIcon,
      buttonText: '前往課程派發',
      color: '#ffab91',
      path: '/teacher/preparation/course-dispatch',
      metric: `${counts.courseDispatches} 個課程派發`,
      completed: preparationStatus.hasCourseDispatch,
      disabled: !(preparationStatus.hasStudents && preparationStatus.hasLessonPackage && preparationStatus.hasCommunity)
    }
  ]), [counts, preparationStatus]);

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'home', navigate);

  return (
    <div className="teacher-layout preparation-home-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase={currentPhase || 'preparation'}
          onPhaseChange={(phase) => {
            if (onPhaseChange) {
              onPhaseChange(phase);
            } else {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">課前準備</h1>
        </div>

        {/* 貓頭鷹博士陪你備課 */}
        <Card sx={{ mb: 3, overflow: 'hidden', background: 'linear-gradient(135deg, #fff7e6 0%, #fffaf0 100%)', border: '1px solid #ffe0b2' }}>
          <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 3 }, alignItems: 'center', py: { xs: 2.5, md: 3 } }}>
            <Box
              component="img"
              src={OwlMascot}
              alt="貓頭鷹博士"
              sx={{
                width: { xs: '140px', md: '160px' },
                height: 'auto',
                objectFit: 'contain'
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                準備開課！
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                先從學生管理開始，填好名單、挑好內容、建好班級，最後派發課程就能準備開課。下面的每張卡片都會告訴你完成條件。
              </Typography>
              <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={
                      preparationStatus.hasCourseDispatch
                        ? '所有準備都完成，可以進入課中實施囉！'
                        : '把卡片上的條件都點亮，就能進入課中實施 🎯'
                    }
                    color={preparationStatus.hasCourseDispatch ? 'success' : 'default'}
                    sx={{ px: 1.5, py: 1, fontWeight: 'bold' }}
                    variant={preparationStatus.hasCourseDispatch ? 'filled' : 'outlined'}
                  />
                  <Chip
                    clickable
                    icon={<HelpOutlineIcon />}
                    onClick={() => {
                      setTourStep(0);
                      setTourOpen(true);
                    }}
                    label="播放導覽"
                    variant={preparationStatus.hasCourseDispatch ? 'filled' : 'outlined'}
                    color={preparationStatus.hasCourseDispatch ? 'success' : 'default'}
                    sx={tourChipSx}
                  />
                </Stack>
             
            </Box>
          </CardContent>
        </Card>

        {/* 步驟卡片 */}
        <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
          {stepCards.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <Grid item xs={12} sm={6} lg={3} key={step.key}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '16px',
                    borderColor: step.completed ? '#a5d6a7' : '#e0e0e0',
                    backgroundColor: step.completed ? '#f1f8e9' : '#ffffff',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      boxShadow: '0 12px 26px rgba(0,0,0,0.08)',
                      transform: 'translateY(-3px)',
                      transition: 'all 0.2s ease'
                    },
                    '&:not(:last-of-type)::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      right: { xs: '50%', lg: '-18px' },
                      transform: { xs: 'translate(50%, 0) rotate(90deg)', lg: 'translateY(-50%)' },
                      width: { xs: '36px', lg: '32px' },
                      height: '2px',
                      backgroundColor: '#cfd8dc',
                      display: { xs: 'none', sm: 'none', lg: 'block' }
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 1.5,
                      py: { xs: 2.5, md: 3 }
                    }}
                  >
                    <Chip
                      label={`Step ${index + 1}`}
                      size="small"
                      sx={{
                        alignSelf: 'center',
                        fontWeight: 'bold',
                        letterSpacing: '0.05em',
                        backgroundColor: step.completed ? '#a5d6a7' : '#e0f2ff',
                        color: step.completed ? '#1b5e20' : '#0277bd'
                      }}
                    />
                    <Avatar
                      sx={{
                        bgcolor: step.color,
                        width: 52,
                        height: 52,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
                      }}
                    >
                      <IconComponent sx={{ fontSize: 30, color: '#4e342e' }} />
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.4 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, px: { xs: 1, md: 1.5 } }}>
                      {step.subtitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6d4c41', lineHeight: 1.6, px: { xs: 1, md: 1.5 } }}>
                      {step.description}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={step.completed ? '完成 🎉' : '尚未完成'}
                        color={step.completed ? 'success' : 'default'}
                        variant={step.completed ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Chip
                        label={loadingStatus ? '讀取中...' : step.metric}
                        variant="outlined"
                      />
                    </Stack>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', mt: 0.5 }}>
                      <Button
                        variant="contained"
                        color={step.completed ? 'success' : 'primary'}
                        onClick={() => navigate(step.path)}
                        disabled={step.disabled}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'bold',
                          borderRadius: '999px',
                          px: 1.5,
                          py: 0.6,
                          fontSize: '0.8rem',
                          mx: 'auto',
                          width: '70%'
                        }}
                      >
                        {step.buttonText}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Dialog
          open={tourOpen}
          onClose={handleTourSkip}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            {tourSteps[tourStep].title}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {currentStepNumber} / {totalSteps}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ mt: 1, borderRadius: 999, height: 8 }}
            />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography>{tourSteps[tourStep].content}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTourSkip} color="inherit">
              跳過導覽
            </Button>
            <Button
              onClick={() => setTourStep((prev) => Math.max(0, prev - 1))}
              disabled={tourStep === 0}
              color="inherit"
            >
              上一步
            </Button>

            <Button onClick={handleTourNext} variant="contained">
              {tourStep === tourSteps.length - 1 ? '完成' : '下一步'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>

  );
}
